import axios from "axios";
import { authStorage } from "services/authStorage";
import { toastError, toastSuccess } from "lib/toast";
import { getStoredLanguage } from "lib/language";

type ToastMeta = {
  /** Disable any toast for this request */
  toast?: boolean;
  /** Disable success toast only */
  toastSuccess?: boolean;
  /** Disable error toast only */
  toastError?: boolean;
};

type AxiosConfigWithMeta = {
  meta?: ToastMeta;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    Accept: "application/json",
  },
});

if (!process.env.REACT_APP_API_URL) {
  console.error(
    "[api] REACT_APP_API_URL is missing. Chat and other API calls will fail. Set it in front/.env and restart the dev server."
  );
}

api.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = getStoredLanguage();
  const pathname = window.location.pathname;
  // Login/OTP requests must not send a stale portal token from localStorage.
  if (pathname.startsWith("/auth/")) {
    return config;
  }
  const token = authStorage.getTokenForPath(pathname);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    const cfg = (response.config || {}) as any as AxiosConfigWithMeta;
    const meta = cfg.meta || {};
    const method = String((response.config as any)?.method || "get").toLowerCase();
    const isMutation = ["post", "put", "patch", "delete"].includes(method);
    if (isMutation && meta.toast !== false && meta.toastSuccess !== false) {
      const message = (response.data as any)?.message;
      if (message) toastSuccess(message);
    }
    return response;
  },
  (error) => {
    const cfg = (error?.config || {}) as any as AxiosConfigWithMeta;
    const meta = cfg.meta || {};

    const status = error?.response?.status;
    if (status === 401) {
      const pathname = window.location.pathname;
      // Failed login/OTP on auth pages must not clear tokens or redirect.
      if (!pathname.startsWith("/auth/")) {
        const guard = authStorage.guardFromPath(pathname) ?? authStorage.getGuard();
        authStorage.clearToken(guard);
        window.location.href = authStorage.loginPath(guard);
      }
    }

    if (meta.toast !== false && meta.toastError !== false) {
      const data = error?.response?.data;
      const message =
        data?.message ||
        (Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : typeof data?.errors === "object" && data?.errors
          ? Object.values(data.errors)
              .flat()
              .filter(Boolean)
              .slice(0, 4)
              .join("\n")
          : null) ||
        (Array.isArray(data?.data)
          ? data.data.join("\n")
          : typeof data?.data === "object" && data?.data
          ? Object.values(data.data)
              .flat()
              .filter(Boolean)
              .slice(0, 4)
              .join("\n")
          : null) ||
        error?.message;

      toastError(message);
    }
    return Promise.reject(error);
  }
);

export default api;
